<script setup lang="ts">
import { computed, ref, watch } from 'vue';

import ModalForm from '~/components/shared/ModalForm.vue';

import { validatePremium } from '../schemas/premiumValidation';

export interface CessionModalProps {
  entityLabel: string;
  isLoading?: boolean;
  isLoadingSecuritizations: boolean;
  modelValue: boolean;
  premium: number;
  securitizationName: string;
  securitizationOptions: Array<{ label: string; value: string }>;
  selectedFinancingIds: Set<number>;
}

const props = withDefaults(defineProps<CessionModalProps>(), {
  isLoading: false,
});

const emit = defineEmits<{
  'save': [];
  'update:modelValue': [value: boolean];
  'update:premium': [value: number];
  'update:securitizationName': [value: string];
}>();

const isVisible = computed({
  get: () => props.modelValue,
  set: (value: boolean) => emit('update:modelValue', value),
});

const securitizationNameModel = computed({
  get: () => props.securitizationName,
  set: (value: string) => emit('update:securitizationName', value),
});

const premiumModel = computed({
  get: () => props.premium,
  set: (value: number) => emit('update:premium', value),
});

const modalTitle = computed(() => `Finalizar ${props.entityLabel}`);

const premiumError = computed(() => validatePremium(props.premium));

const canSave = computed(() => (
  props.selectedFinancingIds.size > 0
  && !!props.securitizationName
  && !props.isLoading
  && !premiumError.value
));

const hasAttemptedSubmit = ref(false);

const handleSave = () => {
  if (canSave.value) {
    hasAttemptedSubmit.value = false;
    emit('save');
  } else {
    hasAttemptedSubmit.value = true;
  }
};

const handleCancel = () => {
  hasAttemptedSubmit.value = false;
  isVisible.value = false;
};

// Reset validation state when modal opens or closes
watch(isVisible, (newValue) => {
  if (newValue) {
    // Reset when modal opens
    hasAttemptedSubmit.value = false;
  }
});
</script>

<template>
  <ModalForm
    v-model="isVisible"
    :title="modalTitle"
    mode="create"
    :loading="isLoading"
    :primary-button-disabled="!canSave"
    primary-button-text="Criar"
    @save="handleSave"
    @cancel="handleCancel"
  >
    <template #default="{ isViewMode: modalIsViewMode, isLoading: modalIsLoading }">
      <div class="row">
        <div class="col-12 col-md-6 mb-3">
          <label class="form-label required" for="securitization_modal">Securitizadora</label>
          <select
            id="securitization_modal"
            v-model="securitizationNameModel"
            class="form-select"
            :class="{ 'is-invalid': hasAttemptedSubmit && !securitizationName && selectedFinancingIds.size > 0 }"
            :disabled="isLoadingSecuritizations || selectedFinancingIds.size === 0 || modalIsViewMode || modalIsLoading"
          >
            <option value="">Selecione a securitizadora...</option>
            <option
              v-for="option in securitizationOptions"
              :key="option.value"
              :value="option.value"
            >
              {{ option.label }}
            </option>
          </select>
          <div
            v-if="hasAttemptedSubmit && !securitizationName && selectedFinancingIds.size > 0"
            class="invalid-feedback"
          >
            Por favor, selecione a securitizadora.
          </div>
        </div>

        <div class="col-12 col-md-6 mb-3">
          <label class="form-label required" for="premium_modal">Ágio (%)</label>
          <input
            id="premium_modal"
            v-model.number="premiumModel"
            type="number"
            class="form-control"
            :class="{ 'is-invalid': premiumError }"
            step="0.01"
            min="-5"
            max="5"
            placeholder="0.00"
            :disabled="selectedFinancingIds.size === 0 || modalIsViewMode || modalIsLoading"
          />
          <div v-if="premiumError" class="invalid-feedback">
            {{ premiumError }}
          </div>
        </div>
      </div>

      <div v-if="selectedFinancingIds.size > 0" class="alert alert-info">
        <strong>{{ selectedFinancingIds.size }}</strong>
        financiamento(s) selecionado(s) para esta {{ entityLabel.toLowerCase() }}.
      </div>
    </template>
  </ModalForm>
</template>

<style scoped lang="scss">
.form-label {
  font-weight: 600;
  margin-bottom: 0.5rem;
}

.form-control,
.form-select {
  border-radius: 0.375rem;

  &:focus {
    border-color: #86b7fe;
    box-shadow: 0 0 0 0.25rem rgba(13, 110, 253, 0.25);
  }

  &.is-invalid {
    border-color: #dc3545;

    &:focus {
      border-color: #dc3545;
      box-shadow: 0 0 0 0.25rem rgba(220, 53, 69, 0.25);
    }
  }
}

.invalid-feedback {
  display: block;
  font-size: 0.875rem;
  color: #dc3545;
  margin-top: 0.25rem;
}
</style>
